import { IMetaTextColor } from "../src/taskpane/interfaces/ITenantInfo";
import { logger } from "./logger";
import { getTextColor } from "./ThemeHelper";

export interface ColorPair {
    backgroundColor: string;
    textColor: string;
};

const hslToHex = (h: number, s: number, l: number): string => {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = (n: number): string => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0'); // Convert to Hex and ensure 2 digits
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

const generateColor = (colorMap): ColorPair => {
    const isCloseToPreviousHues = (newHue: number, threshold: number = 15): boolean => {
        for (const colorPair of colorMap.values()) {
            const prevHue = parseInt(colorPair.backgroundColor.slice(1, 3), 16);
            if (Math.abs(newHue - prevHue) < threshold) {
                return true;
            }
        }
        return false;
    };

    // Generate a random pastel color
    let hue: number;
    do {
        hue = Math.floor(Math.random() * 360);
        if (hue >= 25 && hue <= 180) {
            hue += 65;
        }
    } while (isCloseToPreviousHues(hue));

    const backgroundColor = hslToHex(hue, 100, 35);
    const textColor = "white";

    return { backgroundColor, textColor };
};

const staticColors: ColorPair[] = [
    { backgroundColor: "#D4E7F6", textColor: "#0068B8" },
    { backgroundColor: "#CAF0CC", textColor: "#437406" },
    { backgroundColor: "#FFEBC0", textColor: "#8F6200" },
    { backgroundColor: "#FABBC3", textColor: "#A4262C" },
    { backgroundColor: "#F5CCCF", textColor: "#AA272B" },
    { backgroundColor: "#C3F8F9", textColor: "#03787C" },
    { backgroundColor: "#FED5D1", textColor: "#A0410D" },
    { backgroundColor: "#CAEEE9", textColor: "#007564" },
    { backgroundColor: "#C3CAF9", textColor: "#183EE7" },
    { backgroundColor: "#D8D8EE", textColor: "#373277" },
    { backgroundColor: "#80C6FF", textColor: "#004E8C" },
    { backgroundColor: "#E6E0F0", textColor: "#7252AA" },
    { backgroundColor: "#E5D2E3", textColor: "#86417B" },
    { backgroundColor: "#E5E5E5", textColor: "#666666" },
];

export const generateColorForValue = (() => {
    const colorMap: Map<string, ColorPair> = new Map();
    let staticIndex = 0;

    return (value: string, metadataTextColors: IMetaTextColor[], isFromText?: boolean, newColumn?: any) => {
        let colorPair: ColorPair = colorMap.get(value);
        try {
            if (!colorPair) {
                if (isFromText) {
                    const findCol = metadataTextColors?.filter(m => m.value?.trim().toLowerCase() === value?.trim().toLowerCase());
                    if (Array.isArray(findCol) && findCol.length > 0) {
                        const hexColor: string = findCol[0].hexColor.trim();
                        colorPair = {
                            backgroundColor: hexColor,
                            textColor: getTextColor(hexColor)
                        }
                        colorMap.set(value, colorPair);
                    } else {
                        colorPair = {
                            backgroundColor: "",
                            textColor: ""
                        }
                        colorMap.set(value, colorPair);
                        return colorPair;
                    }
                } else {
                    const metaColor: IMetaTextColor = metadataTextColors?.filter(m => m.value?.trim())?.find(m => m.value?.trim().toLowerCase() === value?.trim().toLowerCase());
                    if (metaColor && metaColor.hexColor.trimStart().startsWith("#")) {
                        const hexColor: string = metaColor.hexColor.trim();
                        colorPair = {
                            backgroundColor: hexColor,
                            textColor: getTextColor(hexColor)
                        }
                        colorMap.set(value, colorPair);
                    }
                }

                if (!colorPair) {
                    if (staticIndex < staticColors.length) {
                        const staticColor = staticColors[staticIndex];
                        staticIndex++;
                        colorPair = staticColor;
                        colorMap.set(value, staticColor);;
                    } else {
                        colorPair = generateColor(colorMap);
                        colorMap.set(value, colorPair);
                    }
                }
            }
        } catch (error) {
            logger(error, "DisplayValueFormatUtil.ts (generateColorForValue)");
        }
        return colorPair
            ?? (isFromText
                ? { backgroundColor: "", textColor: "" }
                : generateColor(colorMap));
    };
})();